import { ThemeProvider } from '@emotion/react';
import PropTypes from 'prop-types';

import {
  createTheme,
  StyledEngineProvider,
  ThemeProvider as MuiThemeProvider,
} from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#4A50DD',
      50: '#4A50DD',
      dark: '#2C348C',
      100: '#2C348C',
      20: '#CCCDF5',
      5: '#F2F2FD',
      0: '#F8F8FE',
    },
    secondary: {
      main: '#F6881F',
      dark: '#dd6c16',
    },
    accent: {
      main: '#F6881F',
      5: '#FDDFC4',
      50: '#F6881F',
      100: '#DD6C16',
    },
    common: {
      white: '#FFF',
      red: '#DE1C22',
      blue: '#0076A3',
      green: '#14892C',
      darkCharcoal: '#333',
      black: '#000000',
      carouselPink: '#FCEFFC',
      promenade: '#F9E5F9',
      darkOrchid: '#9C2BAD',
      grey: '#8f8d8d',
      platinum: '#E6E6E6',
      darkGrey: '#b0b0b0',
    },
    legacyBlue: {
      5: '#F2FAFD',
      20: '#B1D3E0',
      50: '#0076A3',
    },
    success: {
      main: '#008556',
      5: '#D6F3E2',
      50: '#008556',
      100: '#086343',
    },
    error: {
      main: '#DE1C22',
      5: '#F9D3D4',
      50: '#DE1C22',
      100: '#9F1B1F',
    },
    alert: {
      main: '#DE1C22',
      3: '#FFF5F5',
      5: '#FDEDEE',
      50: '#DE1C22',
      100: '#9F1B1F',
      light: '#FDEDEE',
      dialog: '#FFF5F5',
    },
    grey: {
      0: '#FCFCFC',
      3: '#F7F7F7',
      5: '#E4E4E4',
      10: '#1A1A1A',
      40: '#C7C7C7',
      60: '#A1A1A1',
      80: '#656565',
      100: '#1A1A1A',
    },
    text: {
      primary: '#1A1A1A',
      secondary: '#1A1A1A',
      hint: '#A1A1A1',
      disabled: '#A1A1A1',
    },
  },
  typography: {
    fontFamily: '"Open Sans", Arial, sans-serif',
    fontSize: 14,
    htmlFontSize: 10,
    h1: {
      fontWeight: 600,
      fontSize: '3.6rem',
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 600,
      fontSize: '2.4rem',
      lineHeight: 1.2,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.8rem',
      lineHeight: 1.2,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.4rem',
      lineHeight: 1.2,
    },
    subtitle1: {
      fontSize: '1.6rem',
      fontWeight: 600,
    },
    small: {
      fontSize: '1.2rem',
      fontWeight: 400,
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '1.4rem',
    },
    button: {
      fontSize: '1.4rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
  },
  spacing: 4,
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
        colorPrimary: {
          '&.Mui-checked:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiMenuItem: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '@media (forced-colors: active)': {
            forcedColorAdjust: 'none',
          },
        },
      },
    },
  },
});

export default function CssThemeProvider({ children }) {
  return (
    <StyledEngineProvider injectFirst>
      <MuiThemeProvider theme={theme}>
        <ThemeProvider theme={theme}>
          {children}
        </ThemeProvider>
      </MuiThemeProvider>
    </StyledEngineProvider>
  );
}

CssThemeProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};
